import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Activity,
  BarChart3,
  DollarSign,
  Eye,
  Globe2,
  RefreshCw,
  RotateCcw,
  Store,
  Target,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Funnel,
  FunnelChart,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  Treemap,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "react-toastify";
import { analyticsApi } from "../../../api/analyticsApi";
import { useNotifications } from "../../../context/NotificationContext";
import "./PlatformAnalytics.css";

/* ------------------------------------------------------------------ */
/* Constants + formatting                                              */
/* ------------------------------------------------------------------ */

const COLORS = [
  "#2463eb",
  "#13a88a",
  "#f3a712",
  "#8b5cf6",
  "#e45c75",
  "#61a5c2",
  "#8a6d3b",
  "#93a1ab",
];

const EMPTY_REPORT = {
  summary: {
    uniqueVisitors: 0,
    pageViews: 0,
    storeRequests: 0,
    newClients: 0,
    paidRevenue: 0,
    liveStores: 0,
    conversionRate: 0,
  },
  dailyData: [],
  requestStatuses: [],
  topPages: [],
  sources: [],
  devices: [],
  browsers: [],
  campaigns: [],
  sessionActivity: [],
  sessionEvents: [],
  targets: {
    VisitorSessions: 0,
    PageViews: 0,
    StoreRequests: 0,
    ConversionRate: 0,
  },
};

const sessionDateTimeFormatter = new Intl.DateTimeFormat("en-ZA", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Africa/Johannesburg",
});

const dateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const makeRange = (days) => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - days + 1);
  return { startDate: dateValue(start), endDate: dateValue(end) };
};

const number = (value) => Number(value || 0).toLocaleString();
const money = (value) =>
  `R${Number(value || 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
const statusLabel = (value) =>
  String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
const truncate = (value, length = 12) =>
  String(value).length > length
    ? `${String(value).slice(0, length - 1)}…`
    : value;

const targetFields = [
  { key: "visitorSessions", label: "Visitor sessions", format: number },
  { key: "pageViews", label: "Page views", format: number },
  { key: "storeRequests", label: "Store requests", format: number },
  { key: "revenue", label: "Paid revenue", format: money },
  {
    key: "conversionRate",
    label: "Request conversion",
    format: (value) => `${Number(value || 0).toFixed(1)}%`,
  },
];

const paletteColor = (_, index) => COLORS[index % COLORS.length];

/* ------------------------------------------------------------------ */
/* Visual types (the "change visual" menu)                             */
/* ------------------------------------------------------------------ */

const VIZ_LABELS = {
  column: "Column",
  bar: "Bar",
  line: "Line",
  area: "Area",
  step: "Step line",
  pie: "Pie",
  donut: "Donut",
  radar: "Radar",
  treemap: "Treemap",
  funnel: "Funnel",
  table: "Table",
};

const SHARE_VISUALS = [
  "donut",
  "pie",
  "column",
  "bar",
  "treemap",
  "funnel",
  "radar",
  "table",
];
const RANK_VISUALS = [
  "bar",
  "column",
  "donut",
  "pie",
  "treemap",
  "funnel",
  "radar",
  "table",
];

const VISUAL_OPTIONS = {
  trend: ["area", "line", "step", "column"],
  requests: ["column", "line", "area", "step", "table"],
  devices: SHARE_VISUALS,
  statuses: SHARE_VISUALS,
  pages: RANK_VISUALS,
  sources: SHARE_VISUALS,
  browsers: SHARE_VISUALS,
  campaigns: RANK_VISUALS,
  sessions: ["column", "bar", "donut", "pie", "treemap", "radar", "table"],
};

const DEFAULT_VISUALS = {
  trend: { type: "area", labels: false },
  requests: { type: "column", labels: false },
  devices: { type: "donut", labels: false },
  statuses: { type: "donut", labels: false },
  pages: { type: "bar", labels: false },
  sources: { type: "donut", labels: false },
  browsers: { type: "bar", labels: false },
  campaigns: { type: "bar", labels: false },
  sessions: { type: "column", labels: false },
};

const VizIcon = ({ type, size = 18 }) => {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  switch (type) {
    case "column":
      return (
        <svg {...p}>
          <rect x="4" y="12" width="4" height="8" rx="1" />
          <rect x="10" y="5" width="4" height="15" rx="1" />
          <rect x="16" y="9" width="4" height="11" rx="1" />
        </svg>
      );
    case "bar":
      return (
        <svg {...p}>
          <rect x="4" y="4" width="8" height="4" rx="1" />
          <rect x="4" y="10" width="15" height="4" rx="1" />
          <rect x="4" y="16" width="11" height="4" rx="1" />
        </svg>
      );
    case "line":
      return (
        <svg {...p}>
          <polyline points="3 17 9 10 13 14 21 5" />
        </svg>
      );
    case "area":
      return (
        <svg {...p}>
          <path
            d="M3 18 L9 10 L13 14 L21 5 V20 H3 Z"
            fill="currentColor"
            fillOpacity="0.18"
            stroke="none"
          />
          <polyline points="3 18 9 10 13 14 21 5" />
        </svg>
      );
    case "step":
      return (
        <svg {...p}>
          <polyline points="3 18 8 18 8 12 13 12 13 15 18 15 18 6 21 6" />
        </svg>
      );
    case "pie":
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3v9h9" />
        </svg>
      );
    case "donut":
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="3.8" />
          <path d="M12 3v5.2" />
        </svg>
      );
    case "radar":
      return (
        <svg {...p}>
          <polygon points="12 3 20.5 9.2 17.3 19 6.7 19 3.5 9.2" />
          <polygon points="12 8 16 11 14.6 15.5 9.4 15.5 8 11" />
        </svg>
      );
    case "treemap":
      return (
        <svg {...p}>
          <rect x="3" y="3" width="18" height="18" rx="1.5" />
          <path d="M12 3v18M12 12h9M3 14h9" />
        </svg>
      );
    case "funnel":
      return (
        <svg {...p}>
          <path d="M3 4h18l-6 8v7l-6 2v-9z" />
        </svg>
      );
    case "table":
      return (
        <svg {...p}>
          <rect x="3" y="4" width="18" height="16" rx="1.5" />
          <path d="M3 10h18M3 15h18M9 4v16" />
        </svg>
      );
    default:
      return null;
  }
};

const VisualPicker = ({ value, options, labels, onChange, onLabels }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const canLabel = !["table", "treemap", "funnel"].includes(value);

  return (
    <div className="viz-picker" ref={ref}>
      <button
        type="button"
        className="viz-picker-btn"
        aria-haspopup="true"
        aria-expanded={open}
        title="Change visual type"
        onClick={() => setOpen((current) => !current)}
      >
        <VizIcon type={value} />
        <span>{VIZ_LABELS[value]}</span>
        <svg
          className="caret"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="viz-picker-menu" role="menu">
          <div className="viz-picker-title">Visualization type</div>
          <div className="viz-picker-grid">
            {options.map((option) => (
              <button
                key={option}
                type="button"
                role="menuitemradio"
                aria-checked={value === option}
                className={`viz-option ${value === option ? "active" : ""}`}
                title={VIZ_LABELS[option]}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
              >
                <VizIcon type={option} size={22} />
                <span>{VIZ_LABELS[option]}</span>
              </button>
            ))}
          </div>
          {canLabel && (
            <div className="viz-picker-footer">
              <label className="viz-toggle">
                <input
                  type="checkbox"
                  checked={!!labels}
                  onChange={(event) => onLabels(event.target.checked)}
                />
                Show data labels
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Presentational pieces                                               */
/* ------------------------------------------------------------------ */

const ChartTooltip = ({ active, payload, label, format = number }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="analytics-tooltip">
      <div className="analytics-tooltip-label">
        {label ?? payload[0]?.payload?.name}
      </div>
      {payload
        .filter((item) => item.value !== undefined && item.value !== null)
        .map((item) => (
          <div className="analytics-tooltip-row" key={item.dataKey}>
            <span>
              <i
                style={{
                  background:
                    item.color || item.payload?.fill || item.payload?.color,
                }}
              />
              {item.name}
            </span>
            <b>{format(item.value)}</b>
          </div>
        ))}
    </div>
  );
};

const Sparkline = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  const id = `spark-${color.replace("#", "")}`;
  const rows = data.map((value, index) => ({ index, value }));
  return (
    <ResponsiveContainer width="100%" height={36}>
      <AreaChart data={rows} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.25} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={1.75}
          fill={`url(#${id})`}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const Segmented = ({ options, value, onChange, label }) => (
  <div className="segmented" role="group" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        className={value === option.value ? "active" : ""}
        aria-pressed={value === option.value}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const MetricCard = ({ icon: Icon, label, value, detail, tone, spark }) => (
  <article className={`analytics-metric analytics-metric-${tone}`}>
    <div className="analytics-metric-top">
      <span className="analytics-metric-label">{label}</span>
      <span className="analytics-metric-icon">
        <Icon size={18} aria-hidden="true" />
      </span>
    </div>
    <strong className="analytics-metric-value">{value}</strong>
    <small className="analytics-metric-detail">{detail}</small>
    {spark && spark.length > 1 && (
      <div className="analytics-metric-spark">
        <Sparkline data={spark} color={SPARK_COLORS[tone]} />
      </div>
    )}
  </article>
);

const SPARK_COLORS = {
  blue: "#2463eb",
  purple: "#8b5cf6",
  green: "#13a88a",
  amber: "#d38a08",
};

const ChartPanel = ({ title, hint, actions, children, className = "" }) => (
  <section className={`analytics-panel ${className}`}>
    <div className="analytics-panel-heading">
      <div>
        <h2>{title}</h2>
        {hint && <p>{hint}</p>}
      </div>
      {actions && <div className="analytics-panel-actions">{actions}</div>}
    </div>
    {children}
  </section>
);

const EmptyChart = ({ children = "No data for this period yet." }) => (
  <div className="analytics-empty">{children}</div>
);

/* ------------------------------------------------------------------ */
/* Category chart: one component, many shapes                          */
/* Rows look like { name, value, extra? }                              */
/* ------------------------------------------------------------------ */

const ShareBreakdown = ({ rows, format, donut, labels, colorFor }) => {
  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  const pieRows = rows.filter((row) => Number(row.value) > 0);
  return (
    <div className="analytics-donut-layout">
      <div className="analytics-donut">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieRows}
              dataKey="value"
              nameKey="name"
              innerRadius={donut ? "58%" : 0}
              outerRadius="82%"
              paddingAngle={donut ? 3 : 0}
              stroke="#fff"
              strokeWidth={2}
              label={
                labels
                  ? ({ percent }) => `${(percent * 100).toFixed(0)}%`
                  : false
              }
              labelLine={labels}
            >
              {pieRows.map((row) => (
                <Cell
                  key={row.name}
                  fill={colorFor(row.name, rows.indexOf(row))}
                />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip format={format} />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="analytics-legend">
        {rows.map((row, index) => (
          <li key={row.name}>
            <span>
              <i style={{ background: colorFor(row.name, index) }} />
              {row.name}
            </span>
            <span className="analytics-legend-values">
              <strong>{format(row.value)}</strong>
              <em>
                {total > 0
                  ? `${((Number(row.value) / total) * 100).toFixed(1)}%`
                  : "0.0%"}
              </em>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const VizTable = ({ rows, valueLabel, extraLabel, format, colorFor }) => {
  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  const hasExtra = rows.some((row) => row.extra !== undefined);
  return (
    <div className="analytics-table-wrap">
      <table className="analytics-table">
        <thead>
          <tr>
            <th>Name</th>
            <th className="num">{valueLabel}</th>
            {hasExtra && <th className="num">{extraLabel}</th>}
            <th className="num">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.name}-${index}`}>
              <td title={row.name}>
                <i
                  className="analytics-dot"
                  style={{ background: colorFor(row.name, index) }}
                />
                {row.name}
              </td>
              <td className="num">{format(row.value)}</td>
              {hasExtra && <td className="num">{number(row.extra)}</td>}
              <td className="num">
                {total > 0
                  ? `${((Number(row.value) / total) * 100).toFixed(1)}%`
                  : "0.0%"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TreemapCell = ({
  x,
  y,
  width,
  height,
  index,
  depth,
  name,
  value,
  colorFor,
  format,
}) => {
  if (depth < 1) return null;
  const showText = width > 70 && height > 38;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={colorFor(name, index)}
        stroke="#fff"
        strokeWidth={2}
      />
      {showText && (
        <>
          <text x={x + 8} y={y + 20} fill="#fff" fontSize={12} fontWeight={600}>
            {truncate(name, Math.max(6, Math.floor(width / 8)))}
          </text>
          <text
            x={x + 8}
            y={y + 36}
            fill="#fff"
            fillOpacity={0.9}
            fontSize={11}
          >
            {format(value)}
          </text>
        </>
      )}
    </g>
  );
};

const CategoryChart = ({
  data,
  type,
  name,
  extraLabel = "Sessions",
  format = number,
  color = "#2463eb",
  colorFor = paletteColor,
  labels = false,
  height = 260,
  catWidth = 120,
}) => {
  const axisTick = { fontSize: 11, fill: "#718096" };
  const tip = <ChartTooltip format={format} />;
  const hasValues = data.some((row) => Number(row.value) > 0);

  if (type === "table") {
    return (
      <VizTable
        rows={data}
        valueLabel={name}
        extraLabel={extraLabel}
        format={format}
        colorFor={colorFor}
      />
    );
  }

  if (!hasValues) return <EmptyChart>No data for this period yet.</EmptyChart>;

  if (type === "pie" || type === "donut") {
    return (
      <ShareBreakdown
        rows={data}
        format={format}
        donut={type === "donut"}
        labels={labels}
        colorFor={colorFor}
      />
    );
  }

  if (type === "treemap") {
    const cells = data
      .filter((row) => Number(row.value) > 0)
      .map((row) => ({ name: row.name, value: Number(row.value) }));
    return (
      <ResponsiveContainer width="100%" height={Math.max(height, 260)}>
        <Treemap
          data={cells}
          dataKey="value"
          nameKey="name"
          isAnimationActive={false}
          content={<TreemapCell colorFor={colorFor} format={format} />}
        >
          <Tooltip content={tip} />
        </Treemap>
      </ResponsiveContainer>
    );
  }

  if (type === "funnel") {
    const sorted = data
      .filter((row) => Number(row.value) > 0)
      .sort((a, b) => Number(b.value) - Number(a.value))
      .map((row, index) => ({
        name: row.name,
        value: Number(row.value),
        fill: colorFor(row.name, index),
      }));
    return (
      <ResponsiveContainer width="100%" height={Math.max(height, 260)}>
        <FunnelChart margin={{ top: 8, right: 130, left: 8, bottom: 8 }}>
          <Tooltip content={tip} />
          <Funnel
            dataKey="value"
            nameKey="name"
            data={sorted}
            isAnimationActive={false}
          >
            <LabelList
              position="right"
              dataKey="name"
              fill="#182433"
              stroke="none"
              fontSize={12}
              formatter={(value) => truncate(value, 18)}
            />
          </Funnel>
        </FunnelChart>
      </ResponsiveContainer>
    );
  }

  if (type === "radar") {
    return (
      <ResponsiveContainer width="100%" height={Math.max(height, 280)}>
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke="#e5eaf0" />
          <PolarAngleAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "#718096" }}
            tickFormatter={(value) => truncate(value, 14)}
          />
          <PolarRadiusAxis
            tick={{ fontSize: 10, fill: "#9aa5b4" }}
            tickFormatter={format}
            axisLine={false}
          />
          <Radar
            dataKey="value"
            name={name}
            stroke={color}
            fill={color}
            fillOpacity={0.25}
            dot={{ r: 3, fill: color }}
          />
          <Tooltip content={tip} />
        </RadarChart>
      </ResponsiveContainer>
    );
  }

  /* Cartesian shapes: column, bar, line, step, area */
  const horizontal = type === "bar";
  const isBar = type === "column" || type === "bar";
  const Chart = isBar ? BarChart : type === "area" ? AreaChart : LineChart;
  const chartHeight = horizontal
    ? Math.max(height, data.length * 34 + 20)
    : height;
  const labelList = labels ? (
    <LabelList
      dataKey="value"
      position={horizontal ? "right" : "top"}
      formatter={format}
      fontSize={11}
      fill="#718096"
    />
  ) : null;

  let series;
  if (isBar) {
    series = (
      <Bar
        dataKey="value"
        name={name}
        fill={color}
        radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
      >
        {labelList}
      </Bar>
    );
  } else if (type === "area") {
    series = (
      <Area
        type="monotone"
        dataKey="value"
        name={name}
        stroke={color}
        strokeWidth={2}
        fill={color}
        fillOpacity={0.16}
        dot={{ r: 3 }}
      >
        {labelList}
      </Area>
    );
  } else {
    series = (
      <Line
        type={type === "step" ? "stepAfter" : "monotone"}
        dataKey="value"
        name={name}
        stroke={color}
        strokeWidth={2}
        dot={{ r: 3 }}
      >
        {labelList}
      </Line>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <Chart
        data={data}
        layout={horizontal ? "vertical" : "horizontal"}
        margin={{
          top: labels ? 20 : 8,
          right: horizontal ? 48 : 12,
          left: 0,
          bottom: 0,
        }}
      >
        <CartesianGrid
          stroke="#e8edf3"
          vertical={horizontal}
          horizontal={!horizontal}
        />
        {horizontal ? (
          <>
            <XAxis
              type="number"
              tick={axisTick}
              tickLine={false}
              axisLine={{ stroke: "#d5dce6" }}
              tickFormatter={format}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              width={catWidth}
              interval={0}
              tickFormatter={(value) => truncate(value, 20)}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey="name"
              tick={axisTick}
              tickLine={false}
              axisLine={{ stroke: "#d5dce6" }}
              minTickGap={18}
              tickFormatter={(value) => truncate(value)}
            />
            <YAxis
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              width={46}
              tickFormatter={format}
              allowDecimals={false}
            />
          </>
        )}
        <Tooltip
          content={tip}
          cursor={isBar ? { fill: "#f1f5f9" } : undefined}
        />
        {series}
      </Chart>
    </ResponsiveContainer>
  );
};

/* ------------------------------------------------------------------ */
/* Main dashboard                                                      */
/* ------------------------------------------------------------------ */

const TRENDS = {
  both: [
    { key: "PageViews", name: "Page views", color: "#2463eb" },
    { key: "UniqueVisitors", name: "Visitor sessions", color: "#13a88a" },
  ],
  views: [{ key: "PageViews", name: "Page views", color: "#2463eb" }],
  sessions: [
    { key: "UniqueVisitors", name: "Visitor sessions", color: "#13a88a" },
  ],
};

const toRows = (list, nameKey, valueKey, extraKey, labeller = (v) => v) =>
  (list || []).map((item) => ({
    name: labeller(item[nameKey]),
    value: Number(item[valueKey] || 0),
    ...(extraKey ? { extra: Number(item[extraKey] || 0) } : {}),
  }));

const PlatformAnalytics = () => {
  const { socket } = useNotifications();
  const [dateRange, setDateRange] = useState(() => makeRange(30));
  const [report, setReport] = useState(EMPTY_REPORT);
  const [targetDraft, setTargetDraft] = useState({
    visitorSessions: "0",
    pageViews: "0",
    storeRequests: "0",
    revenue: "0",
    conversionRate: "0",
  });
  const [loading, setLoading] = useState(true);
  const [savingTargets, setSavingTargets] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [visuals, setVisuals] = useState(DEFAULT_VISUALS);
  const [trendMetric, setTrendMetric] = useState("both");
  const [sessionMetric, setSessionMetric] = useState("logins");

  const setVisual = useCallback((key, patch) => {
    setVisuals((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }));
  }, []);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await analyticsApi.getReport(
        dateRange.startDate,
        dateRange.endDate,
      );
      const nextReport = { ...EMPTY_REPORT, ...(response.data || {}) };
      setReport(nextReport);
      setTargetDraft({
        visitorSessions: String(nextReport.targets.VisitorSessions || 0),
        pageViews: String(nextReport.targets.PageViews || 0),
        storeRequests: String(nextReport.targets.StoreRequests || 0),
        revenue: String(nextReport.targets.Revenue || 0),
        conversionRate: String(nextReport.targets.ConversionRate || 0),
      });
    } catch (fetchError) {
      console.error("Could not load platform analytics:", fetchError);
      setError(
        fetchError.response?.data?.error ||
          "Could not load analytics. Check that the analytics database tables have been installed.",
      );
    } finally {
      setLoading(false);
    }
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (!socket) return undefined;
    socket.on("platform:updated", fetchReport);
    socket.on("request:updated", fetchReport);
    socket.on("analytics:updated", fetchReport);
    return () => {
      socket.off("platform:updated", fetchReport);
      socket.off("request:updated", fetchReport);
      socket.off("analytics:updated", fetchReport);
    };
  }, [fetchReport, socket]);

  const summary = report.summary;
  const sessionTotals = report.sessionActivity.reduce(
    (totals, item) => ({
      logins: totals.logins + Number(item.logins || 0),
      logouts: totals.logouts + Number(item.logouts || 0),
    }),
    { logins: 0, logouts: 0 },
  );

  const sparks = useMemo(
    () => ({
      visitors: report.dailyData.map((d) => Number(d.UniqueVisitors || 0)),
      views: report.dailyData.map((d) => Number(d.PageViews || 0)),
      requests: report.dailyData.map((d) => Number(d.StoreRequests || 0)),
    }),
    [report.dailyData],
  );

  const metrics = useMemo(
    () => [
      {
        icon: Users,
        label: "Visitor sessions",
        value: number(summary.uniqueVisitors),
        detail: "Anonymous storefront sessions",
        tone: "blue",
        spark: sparks.visitors,
      },
      {
        icon: Eye,
        label: "Page views",
        value: number(summary.pageViews),
        detail: "Public website pages viewed",
        tone: "purple",
        spark: sparks.views,
      },
      {
        icon: Store,
        label: "Store requests",
        value: number(summary.storeRequests),
        detail: `${number(summary.newClients)} new client accounts`,
        tone: "green",
        spark: sparks.requests,
      },
      {
        icon: DollarSign,
        label: "Paid revenue",
        value: money(summary.paidRevenue),
        detail: "Confirmed payments in this period",
        tone: "blue",
      },
      {
        icon: Globe2,
        label: "Live stores",
        value: number(summary.liveStores),
        detail: "Currently published",
        tone: "green",
      },
      {
        icon: Activity,
        label: "Request rate",
        value: `${Number(summary.conversionRate || 0).toFixed(1)}%`,
        detail: "Requests per visitor session",
        tone: "amber",
      },
    ],
    [summary, sparks],
  );

  /* Normalised rows for CategoryChart */
  const requestRows = useMemo(
    () =>
      report.dailyData.map((d) => ({
        name: String(d.Date).slice(5),
        value: Number(d.StoreRequests || 0),
      })),
    [report.dailyData],
  );
  const deviceRows = useMemo(
    () => toRows(report.devices, "Name", "PageViews", null, statusLabel),
    [report.devices],
  );
  const statusRows = useMemo(
    () => toRows(report.requestStatuses, "name", "value", null, statusLabel),
    [report.requestStatuses],
  );
  const pageRows = useMemo(
    () => toRows(report.topPages, "Name", "PageViews", "Visitors"),
    [report.topPages],
  );
  const sourceRows = useMemo(
    () => toRows(report.sources, "Name", "PageViews", "Visitors"),
    [report.sources],
  );
  const browserRows = useMemo(
    () => toRows(report.browsers, "Name", "PageViews"),
    [report.browsers],
  );
  const campaignRows = useMemo(
    () => toRows(report.campaigns, "Name", "PageViews", "Visitors"),
    [report.campaigns],
  );
  const sessionRows = useMemo(
    () =>
      report.sessionActivity.map((item) => ({
        name: statusLabel(item.role),
        value: Number(item[sessionMetric] || 0),
      })),
    [report.sessionActivity, sessionMetric],
  );

  const saveTargets = async (event) => {
    event.preventDefault();
    const targets = Object.fromEntries(
      targetFields.map(({ key }) => [key, Number(targetDraft[key])]),
    );
    if (
      Object.values(targets).some(
        (value) => !Number.isFinite(value) || value < 0,
      )
    ) {
      setError("Targets must be zero or greater.");
      return;
    }

    try {
      setSavingTargets(true);
      setError("");
      await analyticsApi.saveTargets(targets);
      toast.success("Performance targets saved.");
      await fetchReport();
    } catch (saveError) {
      setError(
        saveError.response?.data?.error ||
          "Could not save performance targets.",
      );
    } finally {
      setSavingTargets(false);
    }
  };

  const setPreset = (days) => setDateRange(makeRange(days));
  const restoreDefaults = () => {
    setVisuals(DEFAULT_VISUALS);
    setTrendMetric("both");
    setSessionMetric("logins");
    setDateRange(makeRange(30));
    setActiveTab("overview");
  };

  const periodLabel = `${dateRange.startDate} to ${dateRange.endDate}`;

  /* Visual type menu for one chart */
  const picker = (key) => (
    <VisualPicker
      value={visuals[key].type}
      labels={visuals[key].labels}
      options={VISUAL_OPTIONS[key]}
      onChange={(type) => setVisual(key, { type })}
      onLabels={(labels) => setVisual(key, { labels })}
    />
  );

  /* Main trend chart (supports several series) */
  const trendType = visuals.trend.type;
  const trendLabels = visuals.trend.labels;
  const trendSeries = TRENDS[trendMetric];
  const renderTrend = () => (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={report.dailyData}
        margin={{ top: trendLabels ? 22 : 8, right: 12, left: 0, bottom: 0 }}
      >
        <defs>
          {trendSeries.map((s) => (
            <linearGradient
              key={s.key}
              id={`trend-${s.key}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor={s.color} stopOpacity={0.22} />
              <stop offset="95%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid stroke="#e8edf3" vertical={false} />
        <XAxis
          dataKey="Date"
          tick={{ fill: "#718096", fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: "#d5dce6" }}
          tickFormatter={(value) => String(value).slice(5)}
          minTickGap={18}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fill: "#718096", fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={42}
        />
        <Tooltip
          content={<ChartTooltip format={number} />}
          cursor={trendType === "column" ? { fill: "#f1f5f9" } : undefined}
        />
        {trendSeries.map((s) => {
          const labelList = trendLabels ? (
            <LabelList
              dataKey={s.key}
              position="top"
              fontSize={11}
              fill="#718096"
            />
          ) : null;
          if (trendType === "column") {
            return (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                fill={s.color}
                radius={[4, 4, 0, 0]}
              >
                {labelList}
              </Bar>
            );
          }
          if (trendType === "area") {
            return (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#trend-${s.key})`}
              >
                {labelList}
              </Area>
            );
          }
          return (
            <Line
              key={s.key}
              type={trendType === "step" ? "stepAfter" : "monotone"}
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              strokeWidth={2}
              dot={{ r: 3 }}
            >
              {labelList}
            </Line>
          );
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );

  const tabs = [
    ["overview", "Overview"],
    ["traffic", "Traffic"],
    ["sessions", "User sessions"],
    ["targets", "Targets"],
  ];

  const activePreset = [7, 30, 90].find((days) => {
    const range = makeRange(days);
    return (
      dateRange.startDate === range.startDate &&
      dateRange.endDate === range.endDate
    );
  });

  return (
    <main className="platform-analytics">
      {loading && report.dailyData.length > 0 && (
        <div className="analytics-refresh-bar" aria-hidden="true" />
      )}

      <header className="analytics-header">
        <div>
          <span className="analytics-eyebrow">PLATFORM PERFORMANCE</span>
          <h1>
            <BarChart3 size={25} aria-hidden="true" />
            Analysis
          </h1>
          <p>
            Understand visitor activity and how it turns into store requests.
          </p>
        </div>
        <div className="analytics-header-actions">
          <button
            type="button"
            className="analytics-btn"
            onClick={restoreDefaults}
            title="Restore the default date range and visuals"
          >
            <RotateCcw size={15} aria-hidden="true" />
            Restore defaults
          </button>
          <button
            type="button"
            className="analytics-btn is-primary"
            onClick={fetchReport}
            disabled={loading}
          >
            <RefreshCw
              size={15}
              className={loading ? "is-spinning" : ""}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>
      </header>

      <section className="analytics-filters" aria-label="Report date range">
        <div className="analytics-date-fields">
          <label>
            From
            <input
              type="date"
              value={dateRange.startDate}
              max={dateRange.endDate}
              onChange={(event) =>
                setDateRange((current) => ({
                  ...current,
                  startDate: event.target.value,
                }))
              }
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={dateRange.endDate}
              min={dateRange.startDate}
              max={dateValue(new Date())}
              onChange={(event) =>
                setDateRange((current) => ({
                  ...current,
                  endDate: event.target.value,
                }))
              }
            />
          </label>
        </div>
        <Segmented
          label="Quick date range"
          value={activePreset}
          onChange={setPreset}
          options={[
            { value: 7, label: "7 days" },
            { value: 30, label: "30 days" },
            { value: 90, label: "90 days" },
          ]}
        />
        <span className="analytics-period">{periodLabel}</span>
      </section>

      <nav
        className="analytics-tabs"
        role="tablist"
        aria-label="Analysis sections"
      >
        {tabs.map(([tab, label]) => (
          <button
            type="button"
            role="tab"
            id={`analytics-tab-${tab}`}
            aria-controls={`analytics-panel-${tab}`}
            aria-selected={activeTab === tab}
            className={activeTab === tab ? "is-active" : ""}
            key={tab}
            onClick={() => setActiveTab(tab)}
          >
            {label}
          </button>
        ))}
      </nav>

      {error && (
        <div className="analytics-error" role="alert">
          {error}
        </div>
      )}

      {loading && !report.dailyData.length ? (
        <div className="analytics-loading" aria-live="polite">
          <span className="analytics-spinner" aria-hidden="true" />
          Loading platform analysis…
        </div>
      ) : (
        <>
          {/* ================= OVERVIEW ================= */}
          {activeTab === "overview" && (
            <div
              id="analytics-panel-overview"
              role="tabpanel"
              aria-labelledby="analytics-tab-overview"
            >
              <section
                className="analytics-metrics"
                aria-label="Key performance metrics"
              >
                {metrics.map((metric) => (
                  <MetricCard key={metric.label} {...metric} />
                ))}
              </section>

              <div className="analytics-main-grid">
                <ChartPanel
                  title="Visitor activity"
                  hint="Visitor sessions and page views by day"
                  className="analytics-trend-panel"
                  actions={
                    <>
                      <Segmented
                        label="Series"
                        value={trendMetric}
                        onChange={setTrendMetric}
                        options={[
                          { value: "both", label: "Both" },
                          { value: "views", label: "Views" },
                          { value: "sessions", label: "Sessions" },
                        ]}
                      />
                      {picker("trend")}
                    </>
                  }
                >
                  {report.dailyData.length ? (
                    <div className="analytics-chart">{renderTrend()}</div>
                  ) : (
                    <EmptyChart>
                      No visitor activity yet. Public website page visits will
                      appear here.
                    </EmptyChart>
                  )}
                  <div className="analytics-legend-note">
                    {trendSeries.map((s) => (
                      <span key={s.key}>
                        <i style={{ background: s.color }} />
                        {s.name}
                      </span>
                    ))}
                  </div>
                </ChartPanel>

                <ChartPanel
                  title="Request funnel"
                  hint="New store requests per day"
                  actions={picker("requests")}
                >
                  <CategoryChart
                    data={requestRows}
                    type={visuals.requests.type}
                    labels={visuals.requests.labels}
                    name="Store requests"
                    color="#8b5cf6"
                    height={230}
                  />
                  <div className="analytics-funnel-note">
                    <span>New client accounts</span>
                    <strong>{number(summary.newClients)}</strong>
                  </div>
                </ChartPanel>
              </div>
            </div>
          )}

          {/* ================= TRAFFIC ================= */}
          {activeTab === "traffic" && (
            <section
              className="analytics-breakdown-grid"
              id="analytics-panel-traffic"
              role="tabpanel"
              aria-labelledby="analytics-tab-traffic"
            >
              <ChartPanel
                title="Visitor devices"
                hint="Page views by device"
                actions={picker("devices")}
              >
                <CategoryChart
                  data={deviceRows}
                  type={visuals.devices.type}
                  labels={visuals.devices.labels}
                  name="Page views"
                  color="#2463eb"
                  colorFor={paletteColor}
                />
              </ChartPanel>

              <ChartPanel
                title="Request status"
                hint="Store request outcomes"
                actions={picker("statuses")}
              >
                <CategoryChart
                  data={statusRows}
                  type={visuals.statuses.type}
                  labels={visuals.statuses.labels}
                  name="Requests"
                  color="#8b5cf6"
                />
              </ChartPanel>

              <ChartPanel
                title="Most visited pages"
                hint="Top public pages by page views"
                actions={picker("pages")}
              >
                <CategoryChart
                  data={pageRows}
                  type={visuals.pages.type}
                  labels={visuals.pages.labels}
                  name="Views"
                  color="#2463eb"
                  catWidth={130}
                />
              </ChartPanel>

              <ChartPanel
                title="Traffic sources"
                hint="Where visitor sessions came from"
                actions={picker("sources")}
              >
                <CategoryChart
                  data={sourceRows}
                  type={visuals.sources.type}
                  labels={visuals.sources.labels}
                  name="Views"
                  color="#13a88a"
                  catWidth={130}
                />
              </ChartPanel>

              <ChartPanel
                title="Browsers"
                hint="Page views by browser"
                actions={picker("browsers")}
              >
                <CategoryChart
                  data={browserRows}
                  type={visuals.browsers.type}
                  labels={visuals.browsers.labels}
                  name="Views"
                  color="#f3a712"
                  catWidth={110}
                />
              </ChartPanel>

              <ChartPanel
                title="Campaigns"
                hint="Tagged campaigns by page views"
                actions={picker("campaigns")}
              >
                {campaignRows.length ? (
                  <CategoryChart
                    data={campaignRows}
                    type={visuals.campaigns.type}
                    labels={visuals.campaigns.labels}
                    name="Views"
                    color="#e45c75"
                    catWidth={130}
                  />
                ) : (
                  <EmptyChart>No tagged campaigns in this period.</EmptyChart>
                )}
              </ChartPanel>
            </section>
          )}

          {/* ================= SESSIONS ================= */}
          {activeTab === "sessions" && (
            <section
              className="analytics-sessions"
              id="analytics-panel-sessions"
              role="tabpanel"
              aria-labelledby="analytics-tab-sessions"
            >
              <div className="analytics-metrics analytics-session-metrics">
                <MetricCard
                  icon={Users}
                  label="Logins"
                  value={number(sessionTotals.logins)}
                  detail="Successful authenticated sign-ins"
                  tone="green"
                />
                <MetricCard
                  icon={Activity}
                  label="Logouts"
                  value={number(sessionTotals.logouts)}
                  detail="Recorded sign-outs"
                  tone="purple"
                />
              </div>
              <ChartPanel
                title="Authenticated sessions"
                hint="Login and logout events in the selected date range, grouped by account role"
                actions={
                  <>
                    <Segmented
                      label="Session metric"
                      value={sessionMetric}
                      onChange={setSessionMetric}
                      options={[
                        { value: "logins", label: "Logins" },
                        { value: "logouts", label: "Logouts" },
                      ]}
                    />
                    {picker("sessions")}
                  </>
                }
              >
                {report.sessionActivity.length ? (
                  <CategoryChart
                    data={sessionRows}
                    type={visuals.sessions.type}
                    labels={visuals.sessions.labels}
                    name={sessionMetric === "logins" ? "Logins" : "Logouts"}
                    color={sessionMetric === "logins" ? "#13a88a" : "#8b5cf6"}
                    catWidth={110}
                  />
                ) : (
                  <EmptyChart>
                    No login or logout events for this period.
                  </EmptyChart>
                )}
              </ChartPanel>
              <ChartPanel
                title="Recent account activity"
                hint="Username and name are recorded from the authenticated account."
              >
                {report.sessionEvents.length ? (
                  <div className="analytics-session-table-wrap">
                    <table className="analytics-session-table">
                      <thead>
                        <tr>
                          <th scope="col">Username</th>
                          <th scope="col">Activity</th>
                          <th scope="col">Role</th>
                          <th scope="col">Date and time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.sessionEvents.map((event) => (
                          <tr key={event.id}>
                            <td>
                              <span className="analytics-session-username">
                                {event.username}
                              </span>
                              {event.fullName && (
                                <span className="analytics-session-name">
                                  {event.fullName}
                                </span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`analytics-session-event is-${event.eventType}`}
                              >
                                {event.eventType === "login"
                                  ? "Logged in"
                                  : "Logged out"}
                              </span>
                            </td>
                            <td>{statusLabel(event.role)}</td>
                            <td>
                              {event.recordedAt
                                ? sessionDateTimeFormatter.format(
                                    new Date(event.recordedAt),
                                  )
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyChart>
                    No identified login or logout events are available for this
                    period. Account activity will appear here from the time
                    identity tracking is deployed.
                  </EmptyChart>
                )}
              </ChartPanel>
            </section>
          )}

          {/* ================= TARGETS ================= */}
          {activeTab === "targets" && (
            <div
              id="analytics-panel-targets"
              role="tabpanel"
              aria-labelledby="analytics-tab-targets"
            >
              <ChartPanel
                title="Performance targets"
                hint={`Set goals for the selected date range (${periodLabel}). A zero target is treated as not set.`}
                className="analytics-target-panel"
              >
                <form className="analytics-target-form" onSubmit={saveTargets}>
                  {targetFields.map(({ key, label, format }) => {
                    const actual =
                      key === "visitorSessions"
                        ? summary.uniqueVisitors
                        : key === "pageViews"
                          ? summary.pageViews
                          : key === "storeRequests"
                            ? summary.storeRequests
                            : key === "revenue"
                              ? summary.paidRevenue
                              : summary.conversionRate;
                    const target = Number(targetDraft[key] || 0);
                    const rawProgress = target
                      ? (Number(actual || 0) / target) * 100
                      : 0;
                    const progress = Math.min(100, rawProgress);
                    return (
                      <label className="analytics-target" key={key}>
                        <span className="analytics-target-label">{label}</span>
                        <input
                          type="number"
                          min="0"
                          step={
                            key === "conversionRate"
                              ? "0.1"
                              : key === "revenue"
                                ? "0.01"
                                : "1"
                          }
                          value={targetDraft[key]}
                          onChange={(event) =>
                            setTargetDraft((current) => ({
                              ...current,
                              [key]: event.target.value,
                            }))
                          }
                        />
                        <small>
                          {format(actual)} /{" "}
                          {target ? format(target) : "No target set"}
                          {target > 0 && (
                            <b className={rawProgress >= 100 ? "is-met" : ""}>
                              {Math.round(rawProgress)}%
                            </b>
                          )}
                        </small>
                        <span
                          className={`analytics-target-track ${rawProgress >= 100 ? "is-met" : ""}`}
                          role="progressbar"
                          aria-label={`${label} target progress`}
                          aria-valuenow={Math.round(progress)}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        >
                          <i style={{ width: `${progress}%` }} />
                        </span>
                      </label>
                    );
                  })}
                  <div className="analytics-target-actions">
                    <button
                      type="submit"
                      className="analytics-btn is-primary"
                      disabled={savingTargets}
                    >
                      <Target size={15} aria-hidden="true" />
                      {savingTargets ? "Saving…" : "Save targets"}
                    </button>
                  </div>
                </form>
              </ChartPanel>
            </div>
          )}

          {activeTab !== "sessions" && (
            <p className="analytics-privacy-note">
              Visitor reporting uses anonymous browser-session identifiers and
              coarse device/browser categories. It does not store names, IP
              addresses, full user-agent strings, or page query parameters
              except sanitized campaign/source tags.
            </p>
          )}
        </>
      )}
    </main>
  );
};

export default PlatformAnalytics;
